import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Tv, Film, ChevronDown, ChevronUp, Star, Users, Eye, Trash2 } from 'lucide-react';

// User badge component - shows small avatar
const UserBadge = ({ user }) => (
  <Avatar className="w-6 h-6 border border-white shadow-sm">
    <AvatarImage src={user?.picture} alt={user?.name} />
    <AvatarFallback className="bg-purple-100 text-purple-700 text-[10px]">
      {user?.name?.charAt(0) || '?'}
    </AvatarFallback>
  </Avatar>
);

// Show card component
const ShowCard = ({ show, rank, onViewDetails, onRemove, onMarkWatched, showRank = true }) => {
  const isMovie = show.media_type === 'movie';
  
  return (
    <div 
      className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors group"
      data-testid={`show-card-${show.tmdb_id}`}
    >
      {showRank && (
        <span className="font-outfit font-bold text-lg text-[#3D4F6F] w-8 text-center flex-shrink-0">
          {rank}
        </span>
      )}
      
      {/* Poster */}
      <div className="w-12 h-16 rounded-lg overflow-hidden flex-shrink-0 shadow-sm">
        {show.poster_path ? (
          <img src={show.poster_path} alt={show.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-slate-200 flex items-center justify-center">
            {isMovie ? <Film className="w-5 h-5 text-slate-400" /> : <Tv className="w-5 h-5 text-slate-400" />}
          </div>
        )}
      </div>
      
      {/* Info */}
      <div className="flex-1 min-w-0">
        <h4 
          className="font-medium text-[#3D4F6F] truncate cursor-pointer hover:text-[#F87171]"
          onClick={() => onViewDetails?.(show)}
        >
          {show.title}
        </h4>
        {show.first_air_date && (
          <p className="text-xs text-[#64748B]">{new Date(show.first_air_date).getFullYear()}</p>
        )}
        {show.vote_average > 0 && (
          <div className="flex items-center gap-1 mt-1">
            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
            <span className="text-xs text-[#64748B]">{show.vote_average.toFixed(1)}</span>
          </div>
        )}
      </div>
      
      {/* User badges */}
      {show.user_badges?.length > 0 && (
        <div className="flex -space-x-2" data-testid={`user-badges-${show.tmdb_id}`}>
          {show.user_badges.map((user, idx) => (
            <UserBadge key={user.user_id || idx} user={user} />
          ))}
        </div>
      )}
      
      {/* List count badge for common shows */}
      {show.list_count && (
        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full flex-shrink-0" data-testid={`list-count-${show.tmdb_id}`}>
          {show.list_count} lists
        </span>
      )}
      
      {/* Actions */}
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {onMarkWatched && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onMarkWatched(show)}
            className="h-8 w-8 p-0 text-green-600 hover:bg-green-50"
          >
            <Eye className="w-4 h-4" />
          </Button>
        )}
        {onRemove && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRemove(show.tmdb_id)}
            className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export function JointListCombinedView({ 
  combinedView, 
  onViewDetails, 
  onRemove, 
  onMarkWatched,
  allMembers 
}) {
  const [expandedUsers, setExpandedUsers] = useState({});
  
  const { common_shows = [], other_shows = {} } = combinedView || {};
  
  const toggleUserExpanded = (userId) => {
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };
  
  const hasCommonShows = common_shows.length > 0;
  const hasOtherShows = Object.keys(other_shows).length > 0;
  
  if (!hasCommonShows && !hasOtherShows) {
    return null;
  }
  
  return (
    <div className="space-y-6">
      {/* Common Shows Section */}
      {hasCommonShows && (
        <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm" data-testid="common-picks-section">
          <CardHeader className="pb-2">
            <CardTitle className="font-outfit text-lg text-[#3D4F6F] flex items-center gap-2" data-testid="common-picks-title">
              <Users className="w-5 h-5 text-purple-600" />
              Common Picks ({common_shows.length})
              <span className="text-xs font-normal text-[#64748B] ml-2">
                Shows on multiple lists, ranked by popularity
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {common_shows.map((show, index) => (
                <ShowCard
                  key={show.tmdb_id}
                  show={show}
                  rank={index + 1}
                  onViewDetails={onViewDetails}
                  onRemove={onRemove}
                  onMarkWatched={onMarkWatched}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Other Shows Section - Per User */}
      {hasOtherShows && (
        <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm" data-testid="other-shows-section">
          <CardHeader className="pb-2">
            <CardTitle className="font-outfit text-lg text-[#3D4F6F] flex items-center gap-2" data-testid="other-shows-title">
              Other Picks
              <span className="text-xs font-normal text-[#64748B] ml-2">
                Shows unique to each member's list
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(other_shows).map(([userId, userData]) => {
                const { user, shows } = userData;
                const isExpanded = expandedUsers[userId];
                const displayShows = isExpanded ? shows : shows.slice(0, 3);
                const hasMore = shows.length > 3;
                
                return (
                  <div key={userId} className="border border-slate-100 rounded-xl p-4" data-testid={`other-shows-user-${userId}`}>
                    {/* User header */}
                    <div className="flex items-center gap-3 mb-3">
                      <Avatar className="w-8 h-8 border-2 border-purple-200">
                        <AvatarImage src={user?.picture} alt={user?.name} />
                        <AvatarFallback className="bg-purple-100 text-purple-700 text-sm">
                          {user?.name?.charAt(0) || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-[#3D4F6F]">{user?.name}'s Picks</p>
                        <p className="text-xs text-[#64748B]">{shows.length} shows not on other lists</p>
                      </div>
                    </div>
                    
                    {/* Shows */}
                    <div className="space-y-2">
                      {displayShows.map((show, index) => (
                        <ShowCard
                          key={show.tmdb_id}
                          show={show}
                          rank={show.user_rank}
                          onViewDetails={onViewDetails}
                          showRank={false}
                        />
                      ))}
                    </div>
                    
                    {/* Expand/Collapse button */}
                    {hasMore && (
                      <Button
                        variant="ghost"
                        className="w-full mt-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                        onClick={() => toggleUserExpanded(userId)}
                        data-testid={`expand-btn-${userId}`}
                      >
                        {isExpanded ? (
                          <>
                            <ChevronUp className="w-4 h-4 mr-2" />
                            Show Less
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4 mr-2" />
                            Show All ({shows.length - 3} more)
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default JointListCombinedView;
